import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiMapPin, FiHeart, FiVideo } from "react-icons/fi";
import { formatMoney } from "../../api";

export function ProgressBar({ percent }) {
  return (
    <div className="h-2 rounded-full bg-white/10 overflow-hidden">
      <div className="h-full bg-brand-gradient rounded-full transition-all" style={{ width: `${percent}%` }} />
    </div>
  );
}

export default function CaseCard({ item, currency, index = 0 }) {
  const cover = item.cover_image || item.images?.[0]?.image;
  const monetary = Number(item.amount_needed) > 0;
  const place = [item.city, item.country].filter(Boolean).join(", ");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: Math.min(index, 6) * 0.06 }}
      whileHover={{ y: -6 }}
      className="group flex flex-col rounded-xl bg-navy-dark border border-accent-light/25 shadow-[0_0_15px_rgba(34,211,238,0.2)] overflow-hidden transition-all duration-300 hover:border-accent-light hover:shadow-[0_0_35px_rgba(34,211,238,0.5)]"
    >
      <Link to={`/foundation/${item.id}`} className="block relative bg-navy overflow-hidden">
        {cover ? (
          <img src={cover} alt={item.title} className="w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="w-full aspect-[4/3] bg-brand-gradient" />
        )}
        <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-navy/85 backdrop-blur text-xs font-semibold text-accent-light">
          {item.category_label}
        </span>
        {(item.video || item.video_url) && (
          <span className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-navy/85 text-white" title="Has a video">
            <FiVideo size={14} />
          </span>
        )}
        {item.status === "fulfilled" && (
          <span className="absolute bottom-3 left-3 px-3 py-1 rounded-full bg-emerald-500 text-xs font-semibold text-white">Fulfilled</span>
        )}
      </Link>

      <div className="p-5 flex flex-col flex-1">
        <Link to={`/foundation/${item.id}`} className="text-lg font-semibold text-white group-hover:text-accent-light transition-colors">
          {item.title}
        </Link>
        <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
          <FiMapPin size={12} /> {item.display_name}{place && ` · ${place}`}
        </p>
        <p className="text-sm text-gray-300 mt-3 line-clamp-3">{item.story}</p>

        <div className="mt-auto pt-4">
          {monetary ? (
            <>
              <ProgressBar percent={item.progress ?? 0} />
              <div className="flex justify-between text-xs mt-2 text-gray-400">
                <span><span className="text-white font-semibold">{currency} {formatMoney(item.amount_raised)}</span> raised</span>
                <span>of {currency} {formatMoney(item.amount_needed)}</span>
              </div>
            </>
          ) : (
            <p className="text-xs text-gray-400">Needs non-monetary help</p>
          )}
          <div className="flex items-center justify-between mt-4">
            <span className="text-xs text-gray-400">{item.supporters_count} supporter{item.supporters_count === 1 ? "" : "s"}</span>
            <Link to={`/foundation/${item.id}`} className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-brand-gradient text-white text-sm font-semibold shadow-md shadow-accent/20">
              <FiHeart size={13} /> Help Now
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
